import { t } from '@trimble/i18n';
import { parseStopValue } from '@trimble/measurements/volume-heatmap-palette';
import { Component, For, createMemo, createSignal, onCleanup } from 'solid-js';
import type { EditableStop, TextureType } from '../../types/heatmap.types';
import {
  EDITABLE_STOP_LAYOUT,
  GRADIENT_STOP_BAND_OFFSET_PX,
  GRADIENT_STOP_BAR_WIDTH_PX,
  GRADIENT_STOP_COLUMN_WIDTH_PX,
  heatmapEditorBarCss,
  type StopLayoutOptions,
  stopHandleWrapperTopPx,
  stopListHeightPx,
} from '../../heatmap-stop-layout';
import { transparencyCheckerboardStyle } from '../../gradient-preview/heatmap-css';
import {
  PALETTE_STOP_FOCUS_COLOR,
  paletteStopFocusDropShadow,
} from '../../palette-stop-focus-styles';
import {
  draftStopValueAtPointer,
  clampPointerClientYToTrack,
  handleTopPxDuringDrag,
  hasExceededDragThreshold,
  pointerMappedValueAtClientY,
  stopValueGrabOffsetInMeters,
} from '../stop-handle-drag';

/** Color Indicator overlay — Figma Frame 15783 / Ellipse 344 + Line 11. */
const HANDLE_WRAPPER_LEFT_PX = 6;
const HANDLE_WRAPPER_WIDTH_PX = 37;
const HANDLE_WRAPPER_HEIGHT_PX = 10;
const HANDLE_GAP_PX = 3;
const HANDLE_SIZE_PX = 10;
const HANDLE_BORDER_PX = 2;
const HALF_DIVISOR = 2;
const HANDLE_LINE_WIDTH_PX = 24;
const HANDLE_LINE_LEFT_PX = HANDLE_SIZE_PX + HANDLE_GAP_PX;
const HANDLE_CIRCLE_TOP_OFFSET_PX = 2;
const HANDLE_Z_INDEX = 3;
const DRAGGING_HANDLE_Z_INDEX = 6;
/** Paints the selected handle above stacked neighbours in read-only mode. */
const SELECTED_HANDLE_Z_INDEX = 5;

const HANDLE_STROKE_COLOR = '#FFFFFF';
const HANDLE_DROP_SHADOW = 'drop-shadow(0px 0px 1.8px rgba(0, 0, 0, 0.72))';

interface GradientStopBarProps {
  /** Stops in value-descending order (canonical store order). */
  readonly stops: readonly EditableStop[];
  readonly textureType: TextureType;
  readonly colorAboveRange?: string;
  readonly colorBelowRange?: string;
  readonly editable: boolean;
  readonly selectedStopId?: string | null;
  readonly draggingStopId?: string | null;
  onStopSelect?(stopId: string): void;
  /** Click offset (px from track top) when the user clicks the bar in edit mode. */
  onBarClick?(offsetY: number): void;
  /** Keyboard activation (Enter/Space) — parent picks a non-duplicate insert value. */
  onBarKeyboardInsert?(): void;
  /** Live draft while the user drags a handle (value in metres). */
  onStopDragDraft?(stopId: string, valueInMeters: number): void;
  /** Commit a dragged handle value on pointer up (value in metres). */
  onStopDragCommit?(stopId: string, valueInMeters: number): void;
  /** Select a stop when the user clicks a handle without dragging. */
  onStopActivate?(stopId: string): void;
}

interface StopHandleIndicatorProps {
  readonly topPx: number;
  readonly color: string;
  readonly testId: string;
  readonly selected: boolean;
  readonly selectable: boolean;
  readonly draggable: boolean;
  readonly dragging: boolean;
  readonly zIndex: number;
  onSelect?(): void;
  onPointerDown?(event: PointerEvent): void;
}

const StopHandleIndicator: Component<StopHandleIndicatorProps> = (props) => {
  const strokeColor = (): string =>
    props.selected || props.dragging
      ? PALETTE_STOP_FOCUS_COLOR
      : HANDLE_STROKE_COLOR;
  const handleFilter = (): string =>
    props.selected || props.dragging
      ? paletteStopFocusDropShadow
      : HANDLE_DROP_SHADOW;
  const wrapperClass = (): string => {
    if (props.draggable) return 'absolute cursor-grab touch-none';
    if (props.selectable) return 'absolute cursor-pointer';
    return 'absolute pointer-events-none';
  };
  const isKeyboardFocusable = (): boolean => props.selectable;

  function handleClick(event: MouseEvent): void {
    if (!props.selectable) return;
    event.stopPropagation();
    props.onSelect?.();
  }

  function handleKeyDown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    if (!props.selectable) return;
    event.preventDefault();
    event.stopPropagation();
    props.onSelect?.();
  }

  function handlePointerDown(event: PointerEvent): void {
    if (!props.draggable) return;
    event.stopPropagation();
    props.onPointerDown?.(event);
  }

  return (
    <div
      class={wrapperClass()}
      style={{
        left: `${String(HANDLE_WRAPPER_LEFT_PX)}px`,
        top: `${String(props.topPx)}px`,
        width: `${String(HANDLE_WRAPPER_WIDTH_PX)}px`,
        height: `${String(HANDLE_WRAPPER_HEIGHT_PX)}px`,
        filter: handleFilter(),
        'z-index': props.zIndex,
        ...(props.dragging ? { cursor: 'grabbing' as const } : {}),
      }}
      data-testid={props.testId}
      data-selected={props.selected ? 'true' : undefined}
      data-dragging={props.dragging ? 'true' : undefined}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onPointerDown={handlePointerDown}
      role={isKeyboardFocusable() ? 'button' : undefined}
      tabIndex={isKeyboardFocusable() ? 0 : undefined}
    >
      <div
        class="absolute left-0 shrink-0 rounded-full box-border"
        style={{
          top: `${String(HANDLE_CIRCLE_TOP_OFFSET_PX)}px`,
          width: `${String(HANDLE_SIZE_PX)}px`,
          height: `${String(HANDLE_SIZE_PX)}px`,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          'background-color': props.color,
          border: `${String(HANDLE_BORDER_PX)}px solid ${strokeColor()}`,
        }}
      />
      <div
        class="absolute shrink-0 box-border"
        style={{
          left: `${String(HANDLE_LINE_LEFT_PX)}px`,
          top: `${String(GRADIENT_STOP_BAND_OFFSET_PX)}px`,
          width: `${String(HANDLE_LINE_WIDTH_PX)}px`,
          height: '0px',
          border: 'none',
          // eslint-disable-next-line @typescript-eslint/naming-convention
          'border-top': `${String(HANDLE_BORDER_PX)}px solid ${strokeColor()}`,
        }}
        data-testid={`${props.testId}-line`}
      />
    </div>
  );
};

interface ActiveDragState {
  readonly stopId: string;
  readonly pointerId: number;
  readonly startClientY: number;
  readonly currentClientY: number;
  readonly startHandleTopPx: number;
  readonly valueGrabOffsetInMeters: number;
  readonly draftValueInMeters: number;
  readonly hasMoved: boolean;
}

interface PendingDragListeners {
  readonly pointerSurface: Document;
  handlePointerMove(event: PointerEvent): void;
  handlePointerEnd(event: PointerEvent): void;
}

export const GradientStopBar: Component<GradientStopBarProps> = (props) => {
  let barTrackEl: HTMLDivElement | undefined;

  const [activeDrag, setActiveDrag] = createSignal<ActiveDragState | null>(
    null
  );

  const editableStopCount = (): number => props.stops.length;

  const barHeightPx = (): number => stopListHeightPx(editableStopCount());

  const overflowOverrides = (): {
    above?: string;
    below?: string;
  } => ({
    above: props.colorAboveRange,
    below: props.colorBelowRange,
  });

  const layoutOptions = (): Partial<StopLayoutOptions> => EDITABLE_STOP_LAYOUT;

  const barBackground = createMemo(() =>
    heatmapEditorBarCss(
      props.stops,
      props.textureType,
      overflowOverrides(),
      layoutOptions()
    )
  );

  function handleTopPx(stop: EditableStop): number {
    return stopHandleWrapperTopPx(stop, props.stops, props.textureType, {
      ...layoutOptions(),
      textureType: props.textureType,
    });
  }

  const handleTopPxByStopId = createMemo(() => {
    const tops = new Map<string, number>();
    const drag = activeDrag();
    for (const stop of props.stops) {
      if (drag !== null && drag.stopId === stop.id && drag.hasMoved) {
        tops.set(
          stop.id,
          handleTopPxDuringDrag(
            drag.startHandleTopPx,
            drag.startClientY,
            drag.currentClientY
          )
        );
        continue;
      }
      tops.set(stop.id, handleTopPx(stop));
    }
    return tops;
  });

  const isDraggable = (): boolean =>
    props.editable &&
    props.onStopDragCommit !== undefined &&
    props.onStopDragDraft !== undefined;

  function handleZIndex(stopId: string, selected: boolean): number {
    if (activeDrag()?.stopId === stopId) return DRAGGING_HANDLE_Z_INDEX;
    if (selected) return SELECTED_HANDLE_Z_INDEX;
    return HANDLE_Z_INDEX;
  }

  function barClickOffsetY(clientY: number): number | null {
    if (!barTrackEl) return null;
    const rect = barTrackEl.getBoundingClientRect();
    return clientY - rect.top;
  }

  function emitBarClickAtOffsetY(offsetY: number): void {
    props.onBarClick?.(offsetY);
  }

  function trackTopPx(): number | null {
    return barTrackEl?.getBoundingClientRect().top ?? null;
  }

  function trackHeightPx(): number | null {
    return barTrackEl?.getBoundingClientRect().height ?? null;
  }

  function clampClientYToTrack(clientY: number): number {
    const top = trackTopPx();
    const height = trackHeightPx();
    if (top === null || height === null) {
      return clientY;
    }
    return clampPointerClientYToTrack(clientY, top, height);
  }

  function valueAtPointer(clientY: number): number {
    const top = trackTopPx();
    if (top === null) return 0;
    return pointerMappedValueAtClientY(
      clientY,
      props.stops,
      top,
      props.textureType
    );
  }

  function capturePointer(target: HTMLElement, pointerId: number): void {
    target.setPointerCapture?.(pointerId);
  }

  function releasePointer(target: HTMLElement, pointerId: number): void {
    if (target.hasPointerCapture?.(pointerId)) {
      target.releasePointerCapture(pointerId);
    }
  }

  function finishDrag(
    drag: ActiveDragState,
    event: PointerEvent,
    target: HTMLElement
  ): void {
    releasePointer(target, event.pointerId);
    setActiveDrag(null);
    if (drag.hasMoved) {
      props.onStopDragCommit?.(drag.stopId, drag.draftValueInMeters);
    }
  }

  let pendingHandleDrag: ActiveDragState | null = null;
  let pendingDragListeners: PendingDragListeners | null = null;

  function removePendingDragListeners(): void {
    if (pendingDragListeners === null) return;
    const { pointerSurface, handlePointerMove, handlePointerEnd } =
      pendingDragListeners;
    pointerSurface.removeEventListener('pointermove', handlePointerMove);
    pointerSurface.removeEventListener('pointerup', handlePointerEnd);
    pointerSurface.removeEventListener('pointercancel', handlePointerEnd);
    pendingDragListeners = null;
  }

  onCleanup(() => {
    removePendingDragListeners();
    pendingHandleDrag = null;
    setActiveDrag(null);
  });

  function handleStopPointerDown(
    stop: EditableStop,
    event: PointerEvent
  ): void {
    if (!isDraggable()) return;
    const target = event.currentTarget as HTMLElement;
    capturePointer(target, event.pointerId);
    const stopValueInMeters = parseStopValue(stop.value);
    const pointerMappedValue = valueAtPointer(event.clientY);
    const valueGrabOffsetInMeters = stopValueGrabOffsetInMeters(
      stopValueInMeters,
      pointerMappedValue
    );
    let dragState: ActiveDragState = {
      stopId: stop.id,
      pointerId: event.pointerId,
      startClientY: event.clientY,
      currentClientY: event.clientY,
      startHandleTopPx: handleTopPx(stop),
      valueGrabOffsetInMeters,
      draftValueInMeters: stopValueInMeters,
      hasMoved: false,
    };
    pendingHandleDrag = dragState;
    props.onStopActivate?.(stop.id);

    removePendingDragListeners();

    const handlePointerMove = (moveEvent: PointerEvent): void => {
      const clientY = clampClientYToTrack(moveEvent.clientY);
      const hasMoved =
        dragState.hasMoved ||
        hasExceededDragThreshold(dragState.startClientY, clientY);
      const nextDraft = draftStopValueAtPointer(
        valueAtPointer(clientY),
        dragState.valueGrabOffsetInMeters
      );
      dragState = {
        ...dragState,
        hasMoved,
        currentClientY: clientY,
        draftValueInMeters: nextDraft,
      };
      pendingHandleDrag = dragState;
      if (hasMoved) {
        setActiveDrag(dragState);
        props.onStopDragDraft?.(stop.id, nextDraft);
      }
    };

    const handlePointerEnd = (endEvent: PointerEvent): void => {
      finishDrag(dragState, endEvent, target);
      pendingHandleDrag = null;
      dragState = {
        ...dragState,
        hasMoved: false,
      };
      removePendingDragListeners();
    };

    const pointerSurface = document;

    pendingDragListeners = {
      pointerSurface,
      handlePointerMove,
      handlePointerEnd,
    };

    pointerSurface.addEventListener('pointermove', handlePointerMove);
    pointerSurface.addEventListener('pointerup', handlePointerEnd);
    pointerSurface.addEventListener('pointercancel', handlePointerEnd);
  }

  function handleBarTrackClick(event: MouseEvent): void {
    if (!isBarClickable()) return;
    const offsetY = barClickOffsetY(event.clientY);
    if (offsetY === null) return;
    emitBarClickAtOffsetY(offsetY);
  }

  function handleBarTrackKeyDown(event: KeyboardEvent): void {
    if (!isBarClickable()) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    if (props.onBarKeyboardInsert !== undefined) {
      props.onBarKeyboardInsert();
      return;
    }
    if (!barTrackEl) return;
    emitBarClickAtOffsetY(
      barTrackEl.getBoundingClientRect().height / HALF_DIVISOR
    );
  }

  const isBarClickable = (): boolean =>
    props.editable &&
    props.onBarClick !== undefined &&
    pendingHandleDrag === null &&
    activeDrag() === null;

  const barClickInsertLabel = t(
    'measurements.volumetric.gradient.barClickInsertStop'
  );

  const showBarInsertTooltip = (): boolean =>
    props.editable && props.onBarClick !== undefined;

  return (
    <div
      class="relative shrink-0"
      style={{
        width: `${String(GRADIENT_STOP_COLUMN_WIDTH_PX)}px`,
        height: `${String(barHeightPx())}px`,
        isolation: 'isolate',
      }}
      data-testid="gradient-stop-bar"
    >
      <div
        class="absolute left-0 top-0 z-0"
        style={{
          width: `${String(GRADIENT_STOP_BAR_WIDTH_PX)}px`,
          height: `${String(barHeightPx())}px`,
        }}
      >
        <modus-wc-tooltip
          content={barClickInsertLabel}
          position="right"
          disabled={!isBarClickable()}
          class="pointer-events-none block h-full w-full [&_div]:pointer-events-auto [&_div]:flex [&_div]:h-full [&_div]:w-full"
        >
          <div
            class="relative h-full w-full overflow-hidden border border-[var(--modus-wc-color-base-200,#e0e1e9)]"
            style={{
              // Prototype uses 4px — do not use Tailwind `rounded` (50% in tc.styles.css).
              // eslint-disable-next-line @typescript-eslint/naming-convention
              'border-radius': '4px',
              ...(isBarClickable() ? { cursor: 'crosshair' as const } : {}),
            }}
            data-testid="gradient-stop-bar-track"
            ref={(el) => {
              barTrackEl = el;
            }}
            role={isBarClickable() ? 'button' : undefined}
            tabIndex={isBarClickable() ? 0 : undefined}
            aria-label={
              showBarInsertTooltip() ? barClickInsertLabel : undefined
            }
            onClick={handleBarTrackClick}
            onKeyDown={handleBarTrackKeyDown}
          >
            <div
              class="absolute inset-0"
              style={transparencyCheckerboardStyle()}
              aria-hidden="true"
            />
            <div
              class="absolute inset-0"
              style={{ background: barBackground() }}
              aria-hidden="true"
            />
          </div>
        </modus-wc-tooltip>
      </div>

      <For each={props.stops}>
        {(stop, index) => (
          <StopHandleIndicator
            topPx={handleTopPxByStopId().get(stop.id) ?? handleTopPx(stop)}
            color={stop.color}
            testId={`gradient-stop-handle-${String(index())}`}
            selected={
              props.selectedStopId === stop.id ||
              props.draggingStopId === stop.id
            }
            // Never mark handles selectable: a select click stopPropagation
            // blocks bar insert and can cancel pointer drag.
            selectable={false}
            draggable={isDraggable()}
            dragging={
              activeDrag()?.stopId === stop.id &&
              activeDrag()?.hasMoved === true
            }
            zIndex={handleZIndex(
              stop.id,
              props.selectedStopId === stop.id ||
                props.draggingStopId === stop.id
            )}
            onSelect={() => {
              props.onStopSelect?.(stop.id);
              props.onStopActivate?.(stop.id);
            }}
            onPointerDown={(event) => handleStopPointerDown(stop, event)}
          />
        )}
      </For>
    </div>
  );
};
